import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveAllOrganizationalUnits,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import {
  Identifiable,
  OrganizationalUnit as O,
  OrganizationalUnitFilterEntry,
  allOrganizationalUnitsFilter,
} from "@planzone/dto";

import { ObsIcon } from "@common/assets/obsIcon";
import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Loader } from "../loader";
import { Section } from "../section";
import { Props } from "./types";
import { OrganizationalUnit } from "./organizationalUnit";
import styles from "./styles.scss";

const SEARCH_DELAY = 300;

type Entries = OrganizationalUnitFilterEntry[];

// Lists every unit, searched and paginated by the back: the OBS tree only holds the filtered units.
// The filter only holds the clicked units, picked or unchecked: each unit takes the state of its closest
// entry, itself or an ancestor, so checking a unit checks its whole subtree, even the pages not loaded yet.
function OrganizationalUnits({ search, filter, setFilter, isOpen }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    organizationalUnits,
    isRequestPending,
    filter: unitsFilter,
    totalPages,
    retrieveAllOrganizationalUnits,
  } = useRetrieveAllOrganizationalUnits();
  // The units come in tree order with their ancestors, so every loaded unit can reach its roots.
  const unitsByUuid = React.useMemo(() => {
    const map = new Map<string, Identifiable<O>>();

    (filter ?? []).forEach((entry) => map.set(entry.uuid, entry));
    organizationalUnits.forEach((unit) => map.set(unit.uuid, unit));

    return map;
  }, [organizationalUnits, filter]);
  const parentOf = (uuid: string) => unitsByUuid.get(uuid)?.parentUuid;
  const depthOf = (uuid: string) => {
    let depth = 0;

    for (let parent = parentOf(uuid); parent; parent = parentOf(parent))
      depth++;

    return depth;
  };
  const isCheckedIn = (entries: Entries, uuid?: string): boolean => {
    for (let current = uuid; current; current = parentOf(current)) {
      const entry = entries.find((e) => e.uuid === current);

      if (entry) return !entry.isExcluded;
    }

    return false;
  };
  const isDescendant = (uuid: string, ancestorUuid: string) => {
    for (let parent = parentOf(uuid); parent; parent = parentOf(parent))
      if (parent === ancestorUuid) return true;

    return false;
  };
  // The clicked unit overrides its subtree; its entry is only kept when it differs from what it inherits.
  const toggleOrganizationalUnit = (organizationalUnit: Identifiable<O>) => {
    const entries = filter ?? [];
    const isChecked = isCheckedIn(entries, organizationalUnit.uuid);
    const others = entries.filter(
      ({ uuid }) =>
        uuid !== organizationalUnit.uuid &&
        !isDescendant(uuid, organizationalUnit.uuid),
    );
    const isInherited = isCheckedIn(others, organizationalUnit.parentUuid);

    setFilter(
      !isChecked === isInherited
        ? others
        : [
            ...others,
            {
              uuid: organizationalUnit.uuid,
              name: organizationalUnit.name,
              rankKey: organizationalUnit.rankKey,
              parentUuid: organizationalUnit.parentUuid,
              isExcluded: isChecked,
            },
          ],
    );
  };

  React.useEffect(() => {
    const timeout = window.setTimeout(
      () =>
        retrieveAllOrganizationalUnits({
          planzone,
          filter: { ...allOrganizationalUnitsFilter, name: search },
        }),
      SEARCH_DELAY,
    );

    return () => window.clearTimeout(timeout);
  }, [search]);

  return (
    <Section
      header={
        <div className={styles.header}>
          <ObsIcon className={styles.icon} />
          <p className={styles.title}>
            {t("common.filterMenu.organizationalUnits.title")}
          </p>
        </div>
      }
      list={
        <div className={styles.scrollPane}>
          <InfiniteScroll canFetchOnIdle={!isRequestPending}>
            {() => (
              <div className={styles.list}>
                {organizationalUnits.map((organizationalUnit) => (
                  <OrganizationalUnit
                    key={organizationalUnit.uuid}
                    organizationalUnit={organizationalUnit}
                    depth={depthOf(organizationalUnit.uuid)}
                    isSelected={isCheckedIn(
                      filter ?? [],
                      organizationalUnit.uuid,
                    )}
                    toggle={() => toggleOrganizationalUnit(organizationalUnit)}
                  />
                ))}
                {isRequestPending && <Loader />}
                <Flag
                  name="allOrganizationalUnitsFlag"
                  retrieveItems={() =>
                    !isRequestPending &&
                    retrieveAllOrganizationalUnits({
                      planzone,
                      filter: unitsFilter,
                    })
                  }
                  isDisabled={
                    isRequestPending ||
                    unitsFilter.page === 0 ||
                    unitsFilter.page >= totalPages
                  }
                />
              </div>
            )}
          </InfiniteScroll>
        </div>
      }
      isOpen={isOpen}
    />
  );
}

export { OrganizationalUnits };
